import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router";
import { z } from "zod";
import { authErrorMessage, useAuth } from "@/auth/AuthProvider";
import { ErrorPanel } from "@/components/ErrorPanel";
import { Spinner } from "@/components/Spinner";
import { Copy } from "@/copy/copy";
import { AuthLayout, FieldError } from "./AuthLayout";
import { usePostAuthRedirect } from "./usePostAuthRedirect";

export const signUpSchema = z
  .object({
    email: z
      .string()
      .trim()
      .min(1, "Enter your email.")
      .email("That doesn't look like an email address."),
    password: z.string().min(8, "Choose a password with at least 8 characters."),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    message: "The two passwords don't match.",
    path: ["confirm"],
  });
type Values = z.infer<typeof signUpSchema>;

export function SignUpPage() {
  const { ready, signUp } = useAuth();
  const [params] = useSearchParams();
  const { redirecting, userError, retry } = usePostAuthRedirect("/start");
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { email: "", password: "", confirm: "" },
  });

  const onSubmit = handleSubmit(async ({ email, password }) => {
    setFormError(null);
    try {
      await signUp(email, password);
    } catch (error) {
      setFormError(authErrorMessage(error));
    }
  });

  const query = params.toString() ? `?${params.toString()}` : "";

  return (
    <AuthLayout title="auth.signup.h1" sub="auth.signup.sub" testId="signup-page">
      {!ready ? (
        <Spinner label="Loading" />
      ) : redirecting ? (
        userError ? (
          <ErrorPanel error={userError} onRetry={() => retry()} />
        ) : (
          <Spinner label="Setting up your account" />
        )
      ) : (
        <form onSubmit={onSubmit} noValidate>
          <label className="wp-field">
            Email
            <input
              type="email"
              autoComplete="email"
              {...register("email")}
              aria-invalid={!!errors.email}
              data-testid="signup-email"
            />
            <FieldError message={errors.email?.message} />
          </label>
          <label className="wp-field">
            Password
            <input
              type="password"
              autoComplete="new-password"
              {...register("password")}
              aria-invalid={!!errors.password}
              data-testid="signup-password"
            />
            <FieldError message={errors.password?.message} />
          </label>
          <label className="wp-field">
            Password again
            <input
              type="password"
              autoComplete="new-password"
              {...register("confirm")}
              aria-invalid={!!errors.confirm}
              data-testid="signup-confirm"
            />
            <FieldError message={errors.confirm?.message} />
          </label>
          {formError && (
            <p className="wp-form-err" role="alert" data-testid="signup-error">
              {formError}
            </p>
          )}
          <button
            type="submit"
            className="wp-btn"
            disabled={isSubmitting}
            data-testid="signup-submit"
          >
            {isSubmitting ? "Creating your account…" : "Create my account"}
          </button>
          <Copy k="auth.signup.legal" as="p" className="wp-note" data-testid="signup-legal" />
          <div className="wp-auth-links">
            <span>
              Already have an account? <Link to={`/signin${query}`}>Sign in</Link>
            </span>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
